import { GraphDto } from "@custom-types/graphs"
import { graphAtom } from "@features/run/RunGroupDialog/atoms"
import { useSetAtom } from "jotai"
import { DefaultValues, FieldValues, UseFormReset } from "react-hook-form"
import { useEffect, useRef } from "react"

type UseRunGroupFormSyncParams<TFormValues extends FieldValues> = {
  graph: GraphDto
  reset: UseFormReset<TFormValues>
  defaultValues: DefaultValues<TFormValues>
}

/**
 * Keeps RunGroupForm state aligned with graph changes.
 * react-hook-form applies defaultValues only on initial mount, and jotai hydration is one-time,
 * so we explicitly reset the form and refresh graphAtom when the selected graph changes.
 */
export const useRunGroupFormSync = <TFormValues extends FieldValues>({
  graph,
  reset,
  defaultValues,
}: UseRunGroupFormSyncParams<TFormValues>) => {
  const setGraph = useSetAtom(graphAtom)
  const previousGraphKeyRef = useRef<string | null>(null)

  useEffect(() => {
    setGraph(graph)

    return () => setGraph(null)
  }, [graph, setGraph])

  useEffect(() => {
    if (previousGraphKeyRef.current === graph.key) {
      return
    }

    previousGraphKeyRef.current = graph.key
    reset(defaultValues)
  }, [graph.key, defaultValues, reset])
}
